import { App } from '@capacitor/app'
import { useEffect } from 'react'

import { isNative } from '@/shared/api/platform'
import { safeInternalPath } from '@/shared/lib/safeInternalPath'

/** Dominios cuyos enlaces abren la app (los mismos que el entitlement de iOS y el manifest). */
const APP_HOSTS = new Set(['matchplay.es', 'www.matchplay.es'])

/**
 * Ruta interna de un enlace de matchplay.es que ha abierto la app (Universal Links en iOS,
 * App Links en Android). `null` si el enlace es de otro dominio o no es una ruta segura.
 */
export function appLinkPath(url: string): string | null {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:' || !APP_HOSTS.has(parsed.hostname)) return null
    return safeInternalPath(`${parsed.pathname}${parsed.search}${parsed.hash}`)
  } catch {
    return null
  }
}

/**
 * Enlaces que abren la app (spec 2026-11-02-enlaces-abren-app): al tocar un enlace de
 * matchplay.es con la app instalada, el sistema abre la app y avisa con `appUrlOpen`; aquí se
 * navega a esa pantalla. Si la app estaba cerrada, el aviso llega al arrancar. No-op en web.
 * Se monta una vez desde `App`.
 */
export function useAppUrlOpen(): void {
  useEffect(() => {
    if (!isNative()) return
    const handlePromise = App.addListener('appUrlOpen', ({ url }) => {
      const path = appLinkPath(url)
      if (!path) return
      void import('@/app/router').then(({ router }) => router.navigate(path))
    })
    return () => {
      void handlePromise.then((handle) => handle.remove())
    }
  }, [])
}
